import { motion } from 'framer-motion';
import { TrendingUp, Clock, BarChart } from 'lucide-react';
import { useWebsite } from '../../contexts/WebsiteContext';
import { useState, useEffect } from 'react';

interface SocialProofContent {
  title: string;
  stats: Array<{
    value: string;
    label: string;
    icon: string;
  }>;
  industryInsights: Array<{
    title: string;
    description: string;
    stats: string;
    source: string;
  }>;
  brands: string[];
}

const defaultContent: SocialProofContent = {
  title: "Vision, Mission & Solution",
  stats: [
    { 
      value: "500+",
      label: "Premium Vehicles in database",
      icon: "BarChart"
    },
    { 
      value: "21%",
      label: "Market Penetration within 5 years",
      icon: "TrendingUp"
    },
    { 
      value: "24/7",
      label: "Support Available",
      icon: "Clock"
    }
  ],
  industryInsights: [
    {
      title: "The Challenge",
      description: "Luxury fleet operators lose £150,000+ annually due to inefficient management systems and manual processes.",
      stats: "68% of luxury fleet managers report struggling with legacy systems",
      source: "Fleet Industry Report 2023"
    },
    {
      title: "The Risk",
      description: "Premium vehicle fleets face 3x higher security risks and maintenance costs compared to standard fleets.",
      stats: "£45,000 average annual loss per vehicle due to suboptimal maintenance",
      source: "Luxury Automotive Market Analysis"
    },
    {
      title: "The Solution",
      description: "Our platform reduces operational costs by up to 35% while enhancing fleet utilization by 40%.",
      stats: "Targeting increased ROI and centralized operations and data",
      source: "Internal Performance Data"
    }
  ],
  brands: [
    "Rolls-Royce",
    "Bentley",
    "Aston Martin",
    "Ferrari",
    "Lamborghini",
    "Porsche"
  ]
};

const iconMap = {
  BarChart,
  TrendingUp,
  Clock
};

interface WebsiteContextType {
  isSectionVisible: (section: string) => boolean;
  getContent: (key: string) => Promise<any>;
}

export default function SocialProof() {
  const { getContent } = useWebsite() as unknown as WebsiteContextType;
  const [content, setContent] = useState<SocialProofContent>(defaultContent);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const data = await getContent('social-proof');
        if (data && data.length > 0) {
          setContent(data[0].content as SocialProofContent);
        }
      } catch (error) {
        console.error('Error loading social proof content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [getContent]);

  if (isLoading) {
    return (
      <section id="social-proof" className="py-20 bg-zinc-900">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="social-proof" className="py-20 bg-zinc-900">
      {/* Stats Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-[90%] 2xl:max-w-[1800px] mx-auto px-4 sm:px-6 mb-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {content.stats.map((stat, index) => {
            const Icon = iconMap[stat.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px 0 rgba(251, 191, 36, 0.1)",
                }}
                transition={{ 
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }}
                className="text-center p-10 bg-zinc-800/50 rounded-2xl border border-zinc-700
                         hover:border-amber-500/30 transition-colors duration-300"
              >
                <motion.div 
                  className="flex justify-center mb-6"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="p-3 rounded-lg bg-amber-400/10">
                    {Icon && <Icon className="w-7 h-7 text-amber-400" />}
                  </div>
                </motion.div>
                <motion.div 
                  className="text-3xl font-light tracking-tighter bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-400 mt-3 text-xs tracking-wide font-light">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Industry Insights */}
      <div className="max-w-[90%] 2xl:max-w-[1800px] mx-auto px-4 sm:px-6">
        <motion.h2 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="heading-jaguar text-center mb-12 text-white"
        >
          {content.title}
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {content.industryInsights.map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 0 30px 0 rgba(251, 191, 36, 0.1)",
              }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 20,
                delay: index * 0.1 
              }}
              className="bg-zinc-800/50 p-10 rounded-2xl border border-zinc-700
                       hover:border-amber-500/30 transition-colors duration-300
                       hover:bg-zinc-800/70"
            >
              <motion.h3 
                className="text-jaguar text-lg text-amber-400 mb-4"
                whileHover={{ scale: 1.05, x: 10 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {insight.title}
              </motion.h3>
              <p className="body-jaguar text-gray-300 text-sm mb-6">{insight.description}</p>
              <motion.div 
                className="text-jaguar text-sm text-white mb-3"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {insight.stats}
              </motion.div>
              <div className="text-xs text-gray-500 font-light tracking-wide">{insight.source}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Trusted Brands */}
      <div className="my-40 max-w-[90%] 2xl:max-w-[1800px] mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-jaguar text-sm text-gray-400 tracking-widest uppercase">Specialized in Premium Brands</span>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-12"
        >
          {content.brands.map((brand, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.1,
                color: "#fbbf24",
              }}
              transition={{ 
                type: "spring",
                stiffness: 400,
                damping: 17,
                delay: index * 0.1 
              }}
              className="text-gray-500 text-sm font-light tracking-widest cursor-pointer"
            >
              {brand}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
} 